import { BrowserRouter as Router, Routes, Route, useLocation } from 'react-router-dom';
import { useEffect } from 'react';
import { HelmetProvider } from 'react-helmet-async';

// Pages
import Home from './pages/Home';
import About from './pages/About';
import Contact from './pages/Contact';
import AccountAanvragen from './pages/AccountAanvragen';
import Webshop from './pages/Webshop';
import Privacy from './pages/Privacy';
import Retourneren from './pages/Retourneren';
import Downloads from './pages/Downloads';
import NotFound from './pages/NotFound';

// Components
import Header from './components/Header';
import Footer from './components/Footer';
import UspBar from './components/UspBar';

// Styles
import './styles/style.css';

const LOGIN_STORAGE_KEY = "bakaWebshopLoggedIn";
const BAKA_TRANSLATOR_SRC = "https://timmers.bakaglass.nl/bundles/bazingajstranslation/js/translator.min.js";
const BAKA_WEBSHOP_SRC = "https://timmers.bakaglass.nl/webshop-embed";
const BAKA_TRANSLATIONS_SRC = "https://timmers.bakaglass.nl/translations";

const ProtectedRoute = ({ children }) => {
  const isLoggedIn = sessionStorage.getItem("bakaWebshopLoggedIn") === "true";

  if (!isLoggedIn) {
    return (
      <div style={{
        padding: '100px 20px',
        textAlign: 'center',
        minHeight: '60vh',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center'
      }}>
        <h2>Toegang geweigerd</h2>
        <p>U moet ingelogd zijn om deze pagina te bekijken.</p>
        <button
          onClick={() => window.location.assign('/webshop/account')}
          style={{
            marginTop: '20px',
            padding: '10px 20px',
            backgroundColor: 'var(--timmers-yellow)',
            border: 'none',
            borderRadius: '5px',
            fontWeight: 'bold',
            cursor: 'pointer'
          }}
        >
          Inloggen
        </button>
      </div >
    );
  }

  return children;
};

function App() {
  const location = useLocation();
  const isHome = location.pathname === '/';
  const isWebshop = location.pathname.startsWith('/webshop');
  const TRANSLATOR_REFRESH_KEY = 'bakaTranslatorAutoRefreshAt';
  const TRANSLATOR_REFRESH_COOLDOWN_MS = 10000;

  // Scroll to top on route change
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [location.pathname]);
  const reloadForTranslatorError = (message) => {
    if (!message || !message.includes('Translator is not defined')) {
      return false;
    }

    const lastRefreshAt = Number(sessionStorage.getItem(TRANSLATOR_REFRESH_KEY) || '0');
    const now = Date.now();

    if (now - lastRefreshAt < TRANSLATOR_REFRESH_COOLDOWN_MS) {
      return false;
    }

    console.log('Translator error detected, refreshing page to attempt recovery...');
    sessionStorage.setItem(TRANSLATOR_REFRESH_KEY, String(now));
    window.location.reload();
    return true;
  };

  useEffect(() => {
    const handleWindowError = (event) => {
      const errorMessage = event?.message || event?.error?.message || '';
      reloadForTranslatorError(errorMessage);
    };

    const handleUnhandledRejection = (event) => {
      const errorMessage = event?.reason?.message || String(event?.reason || '');
      reloadForTranslatorError(errorMessage);
    };

    window.addEventListener('error', handleWindowError);
    window.addEventListener('unhandledrejection', handleUnhandledRejection);

    return () => {
      window.removeEventListener('error', handleWindowError);
      window.removeEventListener('unhandledrejection', handleUnhandledRejection);
    };
  }, []);


  useEffect(() => {
    const stylesheetHref = "https://timmers.bakaglass.nl/build/webshop.css";
    let stylesheet = document.querySelector(`link[href="${stylesheetHref}"]`);
    let shouldRemoveStylesheet = false;
    let isCancelled = false;
    const managedScripts = [];

    if (!stylesheet) {
      stylesheet = document.createElement("link");
      stylesheet.href = stylesheetHref;
      stylesheet.rel = "stylesheet";
      document.head.appendChild(stylesheet);
      shouldRemoveStylesheet = true;
    }

    const loadScript = (src) => new Promise((resolve, reject) => {
      let script = document.querySelector(`script[src="${src}"]`);

      const handleLoad = () => {
        if (script) {
          script.dataset.loaded = 'true';
        }
        resolve(script);
      };

      const handleError = () => reject(new Error(`Failed to load script: ${src}`));

      if (script) {
        if (script.dataset.loaded === 'true') {
          resolve(script);
          return;
        }

        script.addEventListener('load', handleLoad, { once: true });
        script.addEventListener('error', handleError, { once: true });
        return;
      }

      script = document.createElement("script");
      script.src = src;
      script.async = false;
      script.dataset.bakaManaged = 'true';
      script.addEventListener('load', handleLoad, { once: true });
      script.addEventListener('error', handleError, { once: true });
      document.body.appendChild(script);
      managedScripts.push(script);
    });

    const loadBakaGlassScripts = async () => {
      try {
        await loadScript(BAKA_TRANSLATOR_SRC);

        if (isCancelled) {
          return;
        }

        if (typeof window.Translator === 'undefined') {
          reloadForTranslatorError('Translator is not defined');
          return;
        }

        await loadScript(BAKA_WEBSHOP_SRC);

        if (isCancelled) {
          return;
        }

        await loadScript(BAKA_TRANSLATIONS_SRC);
      } catch (error) {
        console.error('BakaGlass script loading failed:', error);
      }
    };

    loadBakaGlassScripts();

    return () => {
      isCancelled = true;
      managedScripts.forEach((script) => {
        if (script.parentNode) {
          script.parentNode.removeChild(script);
        }
      });
      if (shouldRemoveStylesheet && stylesheet?.parentNode) {
        stylesheet.parentNode.removeChild(stylesheet);
      }
    };
  }, [isWebshop]);

  // Listen for login success from the iframe
  useEffect(() => {
    const handleMessage = (event) => {

      // Accept messages from the BakaGlass domain
      if (event.origin !== 'https://timmers.bakaglass.nl') {
        return;
      }

      if (event.data && (event.data.loggedIn === true || event.data.type === 'login_success' || event.data === 'login_success')) {
        console.log('Login success detected!');
        sessionStorage.setItem(LOGIN_STORAGE_KEY, 'true');
        document.body.classList.add('is-logged-in');
        window.location.reload();
      } else {
        console.log('Data did not match login success criteria');
      }
    };

    window.addEventListener('message', handleMessage);

    return () => {
      window.removeEventListener('message', handleMessage);
    };
  }, []);

  useEffect(() => {
    const makeProductCardsClickable = () => {
      const productCards = document.querySelectorAll('.webshop-productcard');
      const interactiveSelectors = [
        'a',
        'button',
        'input',
        'select',
        'textarea',
        'label',
        'summary',
        '[role="button"]',
        '[role="link"]',
        '[contenteditable="true"]',
        '.webshop-productcard__button',
        '.webshop-price-show',
        '.webshop-price-detail__price-placeholder',
      ].join(', ');

      productCards.forEach((card) => {
        // Skip if already processed
        if (card.hasAttribute('data-click-handler-attached')) {
          return;
        }

        // Find the product button link inside the card
        const productButton = card.querySelector('.webshop-productcard__button');
        if (!productButton) {
          return;
        }

        // Get the href from the button (it's usually an <a> tag)
        const productLink = productButton.querySelector('a') || productButton.closest('a');
        if (!productLink || !productLink.href) {
          return;
        }

        const productUrl = productLink.href;

        // Mark as processed
        card.setAttribute('data-click-handler-attached', 'true');
        card.style.cursor = 'pointer';

        // Add click handler to the entire card
        card.addEventListener('click', (e) => {
          const target = e.target instanceof Element ? e.target : null;

          if (target?.closest(interactiveSelectors)) {
            return;
          }

          // Navigate to the product page
          if (productUrl && !productUrl.includes('#')) {
            window.location.href = productUrl;
          }
        });
      });
    };

    const DEBOUNCE_DELAY = 100;

    const tryReplace = () => {
      const isUserLoggedIn = sessionStorage.getItem(LOGIN_STORAGE_KEY) === "true";
      document.body.classList.toggle("is-logged-in", isUserLoggedIn);

      // --- TASK D: Make entire product cards clickable ---
      makeProductCardsClickable();
    };

    const handleImageError = (img) => {
      if (img.src && img.src.includes('undefined')) {
        img.style.display = 'none';
        const figure = img.closest('.webshop-figure.webshop-__productcard__figure');
        if (figure) figure.classList.add('is-placeholder');
      }
    };

    let debounceTimer = null;

    // Allow the observer to run on all pages, as Baka components (like featured products)
    // can appear on the home page as well.
    // if (!isWebshop) return undefined;

    const observer = new MutationObserver(() => {
      const newIsLoggedIn = sessionStorage.getItem(LOGIN_STORAGE_KEY) === "true";
      document.body.classList.toggle('is-logged-in', newIsLoggedIn);

      if (debounceTimer) clearTimeout(debounceTimer);
      debounceTimer = setTimeout(() => {
        tryReplace();
        // Check for images
        document.querySelectorAll('.webshop-image.webshop-__productcard__image').forEach(handleImageError);
      }, DEBOUNCE_DELAY);
    });

    observer.observe(document.body, { childList: true, subtree: true, attributes: true });

    // Initial call
    tryReplace();

    // Extra check after a short delay for late-loading BakaGlass elements
    const initialTimeout = setTimeout(tryReplace, 500);
    const secondaryTimeout = setTimeout(tryReplace, 2000);

    return () => {
      observer.disconnect();
      clearTimeout(initialTimeout);
      clearTimeout(secondaryTimeout);
      if (debounceTimer) clearTimeout(debounceTimer);
    };
  }, [location.pathname, isWebshop]);

  return (
    <>
      {/* BakaGlass Settings Component */}
      <baka-settings
        InstanceLink="timmers.bakaglass.nl"
        BaseLink={`${window.location.origin}/webshop`}
        BaseClassName="webshop"
      />

      <Header transparent={isHome} />
      <div className={`App ${isHome ? 'is-home' : ''}`}>
        <Routes>
          <Route path="/" element={<Home />} />
          <Route path="/about" element={<About />} />
          <Route path="/contact" element={<Contact />} />
          <Route path="/account-aanvragen" element={<AccountAanvragen />} />
          <Route path="/privacy" element={<Privacy />} />
          <Route path="/retourneren" element={<Retourneren />} />
          <Route
            path="/downloads"
            element={
              <ProtectedRoute>
                <Downloads />
              </ProtectedRoute>
            }
          />
          <Route path="/webshop/*" element={<Webshop />} />
          <Route path="*" element={<NotFound />} />
        </Routes>
        <UspBar />
        <Footer />
      </div>
    </>
  );
}


function AppWrapper() {
  return (
    <HelmetProvider>
      <Router>
        <App />
      </Router>
    </HelmetProvider>
  );
}

export default AppWrapper;
